/**
 * Kapitel E der Spec — der Punkt, an dem sich entscheidet, ob die Modulanzahl
 * stimmt.
 *
 * Das Luftbild zeigt die Aufsicht. Eine geneigte Flaeche erscheint darin um
 * cos(Neigung) verkuerzt, und zwar nur in Gefaellerichtung; entlang der Traufe
 * stimmt das Mass. Bei 30 Grad fehlen rund 15 Prozent Flaeche, bei 45 Grad 29.
 *
 * Rechenweg (Spec E):
 *   1. Polygon metrisch
 *   2. drehen, bis die Traufkante waagrecht liegt
 *   3. Achse in Gefaellerichtung mit 1/cos(beta) strecken -> wahre Dachmasse
 *   4. Modulraster in dieser Ebene legen
 *   5. zum Zeichnen zurueckstauchen und zurueckdrehen
 */
import { gradInBogen, bogenInGrad, normiereGrad, } from './typen.js';
import { flaecheninhalt, signierteFlaeche } from './polygon.js';
/**
 * Einheitsvektor in Gefaellerichtung, im Grundriss.
 * Azimut 0 = Nord, 90 = Ost. x nach Osten, y nach Norden.
 */
export function gefaelleRichtung(azimutGrad) {
    const a = gradInBogen(azimutGrad);
    return { x: Math.sin(a), y: Math.cos(a) };
}
/**
 * Azimut aus der Traufkante (PV-D10).
 *
 * Der Nutzer markiert eine Kante des Polygons als Traufkante — die untere, zu
 * der das Dach abfaellt. Der Azimut ist die nach aussen zeigende Normale auf
 * diese Kante. Ein Klick, keine Eingabe.
 */
export function azimutAusTraufkante(ring, kanteIndex) {
    const a = ring[kanteIndex % ring.length];
    const b = ring[(kanteIndex + 1) % ring.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.hypot(dx, dy) < 1e-9)
        throw new Error('azimutAusTraufkante: Kante hat keine Länge');
    // Bei einem Ring gegen den Uhrzeigersinn zeigt (dy, -dx) nach aussen.
    const gegenUhrzeiger = signierteFlaeche(ring) > 0;
    const nx = gegenUhrzeiger ? dy : -dy;
    const ny = gegenUhrzeiger ? -dx : dx;
    return normiereGrad(bogenInGrad(Math.atan2(nx, ny)));
}
/**
 * Basis der Dachebene, ausgedrueckt im Grundriss.
 * u laengs der Traufe, v von der Traufe zum First.
 */
function basis(azimutGrad) {
    const gefaelle = gefaelleRichtung(azimutGrad);
    const v = { x: -gefaelle.x, y: -gefaelle.y };
    return { u: { x: v.y, y: -v.x }, v };
}
/** Grundriss -> Dachebene. Streckt die Gefaelleachse mit 1/cos(beta). */
export function nachDachebene(p, lage) {
    const { u, v } = basis(lage.azimutGrad);
    const cos = Math.cos(gradInBogen(lage.neigungGrad));
    if (cos < 1e-6) {
        throw new Error('nachDachebene: bei 90 Grad ist die Fläche im Grundriss eine Linie. ' +
            'Fassaden werden über fassade.ts aufgespannt, nicht projiziert.');
    }
    return {
        x: p.x * u.x + p.y * u.y,
        y: (p.x * v.x + p.y * v.y) / cos,
    };
}
/** Dachebene -> Grundriss. */
export function nachGrundrissebene(p, lage) {
    const { u, v } = basis(lage.azimutGrad);
    const cos = Math.cos(gradInBogen(lage.neigungGrad));
    const vAnteil = p.y * cos;
    return {
        x: p.x * u.x + vAnteil * v.x,
        y: p.x * u.y + vAnteil * v.y,
    };
}
/**
 * Wie weit weicht die Flaeche vom Rechteck ab (PV-D21)?
 *
 * Gemessen wird in der Dachebene und gegen die Traufkante, denn dort liegt das
 * Modulraster: Es laeuft immer laengs der Traufe. Steht der First um ein Grad
 * schief, laeuft die oberste Modulreihe auf neun Metern Dachbreite um fuenfzehn
 * Zentimeter in die Kante — und ein Modul faellt weg, obwohl auf dem Bildschirm
 * reichlich Platz aussieht.
 *
 * Zurueck kommt die groesste Abweichung einer Kante von 0 oder 90 Grad, in
 * Grad. Null heisst: sauberes Rechteck.
 */
export function winkelabweichungGrad(dachebene) {
    if (dachebene.length < 3)
        return 0;
    let groesste = 0;
    for (let i = 0; i < dachebene.length; i++) {
        const a = dachebene[i];
        const b = dachebene[(i + 1) % dachebene.length];
        if (Math.hypot(b.x - a.x, b.y - a.y) < 0.2)
            continue; // Stummel zaehlen nicht
        const winkel = Math.abs(bogenInGrad(Math.atan2(b.y - a.y, b.x - a.x))) % 90;
        groesste = Math.max(groesste, Math.min(winkel, 90 - winkel));
    }
    return groesste;
}
export const ringNachDachebene = (ring, lage) => ring.map((p) => nachDachebene(p, lage));
export const ringNachGrundrissebene = (ring, lage) => ring.map((p) => nachGrundrissebene(p, lage));
/** Wahre Dachlaenge aus der im Luftbild gemessenen (Spec: 8,00 m bei 30 Grad -> 9,24 m). */
export function wahreLaenge(projizierteLaengeM, neigungGrad) {
    return projizierteLaengeM / Math.cos(gradInBogen(neigungGrad));
}
/** Wahre Dachflaeche aus der projizierten Grundflaeche (PV-E02). */
export function wahreFlaeche(projizierteFlaecheM2, neigungGrad) {
    return projizierteFlaecheM2 / Math.cos(gradInBogen(neigungGrad));
}
/**
 * Eine gezeichnete Flaeche durchrechnen: Azimut aus der Traufkante, beide
 * Flaechenangaben, Polygon in der Dachebene.
 */
export function analysiereFlaeche(ringGrundriss, traufkanteIndex, neigungGrad, azimutUeberschrieben) {
    const azimutGrad = azimutUeberschrieben ?? azimutAusTraufkante(ringGrundriss, traufkanteIndex);
    const lage = { azimutGrad, neigungGrad };
    const projiziert = flaecheninhalt(ringGrundriss);
    return {
        azimutGrad,
        neigungGrad,
        projizierteFlaecheM2: projiziert,
        wahreFlaecheM2: wahreFlaeche(projiziert, neigungGrad),
        dachebene: ringNachDachebene(ringGrundriss, lage),
    };
}
//# sourceMappingURL=dachebene.js.map